/**
 * Base config format — stored as pretty-printed JSON text inside a `.base`
 * file's Y.Doc (Y.Text "content"), so bases sync/merge across devices through
 * the same Yjs pipeline as notes and stay visible on disk as plain files.
 */

export type Cmp =
  | 'eq'
  | 'ne'
  | 'contains'
  | 'gt'
  | 'lt'
  | 'gte'
  | 'lte'
  | 'exists'
  | 'missing';

export interface FilterRule {
  prop: string;
  cmp: Cmp;
  value?: string;
}

export interface FilterGroup {
  op: 'and' | 'or';
  rules: (FilterRule | FilterGroup)[];
}

export interface BaseColumn {
  prop: string;
  width?: number;
}

export interface SortKey {
  prop: string;
  dir: 'asc' | 'desc';
}

export interface BaseConfig {
  name: string;
  filters: FilterGroup;
  columns: BaseColumn[];
  sort: SortKey[];
  pageSize?: number;
}

export const CMPS: Cmp[] = ['eq', 'ne', 'contains', 'gt', 'lt', 'gte', 'lte', 'exists', 'missing'];

export const CMP_LABELS: Record<Cmp, string> = {
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
  gt: '>',
  lt: '<',
  gte: '≥',
  lte: '≤',
  exists: 'exists',
  missing: 'missing',
};

/** Pseudo-properties derived from the file entry rather than frontmatter. */
export const FILE_PROPS = ['file.name', 'file.path', 'file.folder', 'file.ctime'];
/** The note's combined tag list (frontmatter + inline), no '#'. */
export const TAGS_PROP = 'tags';

export const isGroup = (r: FilterRule | FilterGroup): r is FilterGroup =>
  typeof r === 'object' &&
  r !== null &&
  Array.isArray((r as FilterGroup).rules) &&
  ((r as FilterGroup).op === 'and' || (r as FilterGroup).op === 'or');

export function defaultConfig(name: string): BaseConfig {
  return {
    name: name || 'New base',
    filters: { op: 'and', rules: [] },
    columns: [{ prop: 'file.name' }, { prop: TAGS_PROP }, { prop: 'file.ctime' }],
    sort: [{ prop: 'file.name', dir: 'asc' }],
    pageSize: 50,
  };
}

export function serializeConfig(c: BaseConfig): string {
  return JSON.stringify(c, null, 2) + '\n';
}

/**
 * Tolerant parse: returns a sanitized BaseConfig, or null when the text is not
 * a JSON object at all (so the UI can offer a reset instead of clobbering).
 * Unknown/malformed pieces are dropped, never thrown on.
 */
export function parseConfig(text: string): BaseConfig | null {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const cfg: BaseConfig = {
    name: typeof o.name === 'string' ? o.name : 'Base',
    filters: sanitizeGroup(o.filters, 0) ?? { op: 'and', rules: [] },
    columns: sanitizeColumns(o.columns),
    sort: sanitizeSort(o.sort),
  };
  if (typeof o.pageSize === 'number' && Number.isFinite(o.pageSize) && o.pageSize > 0) {
    cfg.pageSize = Math.floor(o.pageSize);
  }
  if (cfg.columns.length === 0) cfg.columns = [{ prop: 'file.name' }];
  return cfg;
}

const MAX_GROUP_DEPTH = 4;

function sanitizeGroup(v: unknown, depth: number): FilterGroup | null {
  if (typeof v !== 'object' || v === null || depth > MAX_GROUP_DEPTH) return null;
  const g = v as Record<string, unknown>;
  const op = g.op === 'or' ? 'or' : 'and';
  const rules: (FilterRule | FilterGroup)[] = [];
  if (Array.isArray(g.rules)) {
    for (const r of g.rules) {
      if (typeof r !== 'object' || r === null) continue;
      const rr = r as Record<string, unknown>;
      if (Array.isArray(rr.rules)) {
        const sub = sanitizeGroup(rr, depth + 1);
        if (sub) rules.push(sub);
      } else if (typeof rr.prop === 'string' && CMPS.includes(rr.cmp as Cmp)) {
        const rule: FilterRule = { prop: rr.prop, cmp: rr.cmp as Cmp };
        if (rr.value !== undefined) rule.value = String(rr.value);
        rules.push(rule);
      }
    }
  }
  return { op, rules };
}

function sanitizeColumns(v: unknown): BaseColumn[] {
  if (!Array.isArray(v)) return [];
  const out: BaseColumn[] = [];
  for (const c of v) {
    if (typeof c === 'string') {
      out.push({ prop: c });
    } else if (typeof c === 'object' && c !== null && typeof (c as BaseColumn).prop === 'string') {
      const col: BaseColumn = { prop: (c as BaseColumn).prop };
      const w = (c as BaseColumn).width;
      if (typeof w === 'number' && Number.isFinite(w) && w > 0) col.width = w;
      out.push(col);
    }
  }
  return out;
}

function sanitizeSort(v: unknown): SortKey[] {
  if (!Array.isArray(v)) return [];
  const out: SortKey[] = [];
  for (const s of v) {
    if (typeof s !== 'object' || s === null) continue;
    const ss = s as Record<string, unknown>;
    if (typeof ss.prop !== 'string') continue;
    out.push({ prop: ss.prop, dir: ss.dir === 'desc' ? 'desc' : 'asc' });
  }
  return out;
}

/** Short display label for a column header. */
export function propLabel(prop: string): string {
  switch (prop) {
    case 'file.name':
      return 'Name';
    case 'file.path':
      return 'Path';
    case 'file.folder':
      return 'Folder';
    case 'file.ctime':
      return 'Created';
    case TAGS_PROP:
      return 'Tags';
    default:
      return prop;
  }
}
